import type { ChatModelRunResult } from '@assistant-ui/react';
import { parseJsonEventStream, readUIMessageStream, uiMessageChunkSchema } from 'ai';
import { streamFailureNotice } from '~/utils/assistant-ui/streamFailure';

export async function* readAssistantReply(body: ReadableStream<Uint8Array>): AsyncGenerator<ChatModelRunResult> {
	let failureNotice: string | undefined;

	// Parse SSE → UIMessageChunk stream → progressive UIMessage updates
	const chunkStream = parseJsonEventStream({
		schema: uiMessageChunkSchema,
		stream: body,
	}).pipeThrough(
		new TransformStream({
			transform(chunk, controller) {
				if (!chunk.success) throw chunk.error;
				failureNotice ??= streamFailureNotice(chunk.value);
				controller.enqueue(chunk.value);
			},
		})
	);

	let lastContent: ChatModelRunResult['content'] = [];

	for await (const uiMessage of readUIMessageStream({ stream: chunkStream })) {
		const content = uiMessage.parts
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			.map((p: any) => {
				if (p.type === 'text') return { type: 'text' as const, text: p.text };
				// readUIMessageStream produces named tool parts (type: 'tool-<name>')
				// or dynamic tool parts (type: 'dynamic-tool' with toolName field)
				if (p.type === 'dynamic-tool' || p.type?.startsWith('tool-'))
					return {
						args: p.input ?? {},
						argsText: JSON.stringify(p.input ?? {}),
						result: p.output,
						toolCallId: p.toolCallId,
						toolName: p.toolName ?? p.type.slice(5),
						type: 'tool-call' as const,
					};
				return null;
			})
			.filter((p): p is NonNullable<typeof p> => p !== null);

		if (content.length > 0) {
			lastContent = content;
			yield { content };
		}
	}

	if (failureNotice) {
		yield { content: [...(lastContent ?? []), { text: failureNotice, type: 'text' as const }] };
	}
}
